import React, { useState, useEffect } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDBadge from "components/MDBadge";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Autocomplete,
  TextField,
  CircularProgress
} from "@mui/material";
import CampanhaCreateEdit from "./CampanhaCreateEdit";
import { get, post, postNoResponse } from "utils/WebService";
import { infoAviso, successAlert, warningAlert } from "utils/Functions";
import { useMaterialUIController } from "context";
import { ESCURO } from "assets/coresTema";

export default function CampanhaConsult() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [openModal, setOpenModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Estados para Duplicação
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [itemToDuplicate, setItemToDuplicate] = useState(null);
  const [duplicateNome, setDuplicateNome] = useState("");
  const [duplicateClienteId, setDuplicateClienteId] = useState("");
  const [duplicateSubCarteiraId, setDuplicateSubCarteiraId] = useState("");
  const [clientes, setClientes] = useState([]);
  const [subCarteiras, setSubCarteiras] = useState([]);
  const [duplicating, setDuplicating] = useState(false);

  const fetchData = () => {
    setLoading(true);
    post("/campanha/list", {
      page: 0,
      pageSize: 100,
      sorted: [],
      filtered: []
    }).then(({ data }) => {
      setDataList(data || []);
    }).catch((e) => {
      console.error(e);
    }).finally(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchData();
    get("/pessoa/list-all").then((res) => {
      setClientes(res.list || res.data || (Array.isArray(res) ? res : []));
    });
  }, []);

  const fetchSubCarteiras = (clienteId) => {
    if (!clienteId) {
      setSubCarteiras([]);
      return;
    }
    get(`/pessoa/by-id/${clienteId}`).then((res) => {
      setSubCarteiras(res.subCarteiras || []);
    });
  };

  const handleEdit = (id) => {
    setSelectedId(id);
    setOpenModal(true);
  };

  const handleCreate = () => {
    setSelectedId(null);
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    fetchData();
  };

  const handleDelete = (id) => {
    infoAviso("Deseja realmente excluir esta campanha?", async () => {
      postNoResponse(`/campanha/delete/${id}`).then(() => {
        successAlert("Campanha removida com sucesso!");
        fetchData();
      }).catch((e) => {
        warningAlert("Erro ao excluir campanha.");
      });
    }, () => null);
  };

  const handleDuplicate = (item) => {
    setItemToDuplicate(item);
    setDuplicateNome(`${item.ccNome || ""} (Cópia)`);
    const cliId = item.cliente?.id || "";
    setDuplicateClienteId(cliId);
    const subcId = item.subCarteira?.id || "";
    setDuplicateSubCarteiraId(subcId);
    fetchSubCarteiras(cliId);
    setDuplicateModalOpen(true);
  };

  const confirmDuplicate = () => {
    if (!duplicateNome || !duplicateNome.trim()) {
      warningAlert("Por favor, informe o nome da nova campanha.");
      return;
    }
    setDuplicating(true);
    postNoResponse("/campanha/duplicate", {
      origemId: itemToDuplicate.id,
      novoNome: duplicateNome.trim(),
      novoClienteId: duplicateClienteId || null,
      novaSubCarteiraId: duplicateSubCarteiraId || null,
    }).then(() => {
      successAlert("Campanha duplicada com sucesso!");
      setDuplicateModalOpen(false);
      setItemToDuplicate(null);
      fetchData();
    }).catch((e) => {
      console.error(e);
      warningAlert("Erro ao duplicar campanha.");
    }).finally(() => {
      setDuplicating(false);
    });
  };

  const getTipoLabel = (tipo) => {
    switch (Number(tipo)) {
      case 1: return <MDBadge badgeContent="Global (Devedor)" color="info" variant="gradient" size="sm" />;
      case 2: return <MDBadge badgeContent="Por Faixa de Atraso" color="warning" variant="gradient" size="sm" />;
      case 3: return <MDBadge badgeContent="Marcação Especial" color="secondary" variant="gradient" size="sm" />;
      case 4: return <MDBadge badgeContent="Por Ano de Vencimento" color="success" variant="gradient" size="sm" />;
      default: return "-";
    }
  };

  return (
    <MDBox pt={1} pb={3}>
      <Card sx={{ background: darkMode ? ESCURO.cartao : "#fafafa" }}>
        <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h6" fontWeight="medium" color={darkMode ? "white" : "dark"}>
            Gestão de Campanhas e Regras
          </MDTypography>
          <MDButton variant="gradient" color="info" onClick={handleCreate} startIcon={<Icon>add</Icon>} size="small">
            Nova Campanha
          </MDButton>
        </MDBox>

        <MDBox p={3}>
          <TableContainer sx={{ boxShadow: "none" }}>
            <Table>
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  <TableCell align="left"><MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"}>Nome da Campanha</MDTypography></TableCell>
                  <TableCell align="center"><MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"}>Tipo</MDTypography></TableCell>
                  <TableCell align="center"><MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"}>Vigência</MDTypography></TableCell>
                  <TableCell align="center"><MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"}>Status</MDTypography></TableCell>
                  <TableCell align="center"><MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"}>Ações</MDTypography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center"><MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>Carregando...</MDTypography></TableCell>
                  </TableRow>
                ) : dataList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center"><MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>Nenhuma campanha cadastrada.</MDTypography></TableCell>
                  </TableRow>
                ) : dataList.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <MDBox display="flex" alignItems="center">
                        <MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>
                          {item.ccNome}
                        </MDTypography>
                        {item.cbPadrao && (
                          <MDBox ml={1}>
                            <MDBadge badgeContent="Padrão" color="primary" variant="contained" size="sm" />
                          </MDBox>
                        )}
                      </MDBox>
                    </TableCell>
                    <TableCell align="center">{getTipoLabel(item.cvTipoAplicacao)}</TableCell>
                    <TableCell align="center">
                      <MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>
                        {item.cdInicio ? `${new Date(item.cdInicio).toLocaleDateString()} até ${item.cdFim ? new Date(item.cdFim).toLocaleDateString() : 'Indefinida'}` : "Indefinida"}
                      </MDTypography>
                    </TableCell>
                    <TableCell align="center">
                      <MDTypography variant="button" fontWeight="regular" color={item.cbAtivo ? "success" : "error"}>
                        {item.cbAtivo ? "Ativo" : "Inativo"}
                      </MDTypography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Duplicar Campanha">
                        <IconButton color="secondary" onClick={() => handleDuplicate(item)}>
                          <Icon fontSize="small">content_copy</Icon>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton color="info" onClick={() => handleEdit(item.id)}>
                          <Icon fontSize="small">edit</Icon>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton color="error" onClick={() => handleDelete(item.id)}>
                          <Icon fontSize="small">delete</Icon>
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </MDBox>
      </Card>

      {/* Modal de Duplicação de Campanha */}
      <Dialog
        open={duplicateModalOpen}
        onClose={() => !duplicating && setDuplicateModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? ESCURO.painel : "#ffffff",
            color: darkMode ? "#ffffff" : "inherit"
          }
        }}
      >
        <DialogTitle sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
          Duplicar Campanha
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: darkMode ? ESCURO.divisoria : "#e0e0e0" }}>
          <MDBox mb={2}>
            <MDTypography variant="caption" color={darkMode ? "white" : "text"} display="block">
              Campanha de Origem: <strong>{itemToDuplicate?.ccNome}</strong>
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : "text.secondary" }} display="block">
              Todas as regras, prazos, limites e tabelas de parcelamento serão copiados para a nova campanha.
            </MDTypography>
          </MDBox>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"} display="block" mb={0.5}>
                Nome da Nova Campanha *
              </MDTypography>
              <MDInput
                fullWidth
                size="small"
                value={duplicateNome}
                onChange={(e) => setDuplicateNome(e.target.value)}
                placeholder="Ex: Campanha Banco X (Nova)"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"} display="block" mb={0.5}>
                Cliente
              </MDTypography>
              <Autocomplete
                options={clientes}
                getOptionLabel={(option) => (option ? (option.nomeCompleto || option.ccNome || "") : "")}
                value={clientes.find(c => String(c.id) === String(duplicateClienteId)) || null}
                onChange={(event, newValue) => {
                  const newId = newValue ? newValue.id : "";
                  setDuplicateClienteId(newId);
                  fetchSubCarteiras(newId);
                  setDuplicateSubCarteiraId("");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Selecione o Cliente (opcional - Todos)"
                    fullWidth
                   
                  />
                )}
                noOptionsText="Nenhum cliente encontrado"
              />
            </Grid>

            {subCarteiras && subCarteiras.length > 0 && (
              <Grid item xs={12}>
                <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"} display="block" mb={0.5}>
                  Sub Carteira
                </MDTypography>
                <Autocomplete
                  options={subCarteiras}
                  getOptionLabel={(option) => (option ? (option.ccNome || "") : "")}
                  value={subCarteiras.find(s => String(s.id) === String(duplicateSubCarteiraId)) || null}
                  onChange={(event, newValue) => {
                    setDuplicateSubCarteiraId(newValue ? newValue.id : "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Selecione a Sub Carteira (opcional - Todas)"
                      fullWidth
                     
                    />
                  )}
                  noOptionsText="Nenhuma sub carteira encontrada"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setDuplicateModalOpen(false)}
            disabled={duplicating}
            sx={{
              color: darkMode ? "#ffffff !important" : undefined,
              borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined
            }}
          >
            Cancelar
          </MDButton>
          <MDButton
            variant="contained"
            color="info"
            onClick={confirmDuplicate}
            disabled={duplicating || !duplicateNome.trim()}
            sx={{
              backgroundColor: darkMode ? "#007bff !important" : undefined,
              color: "#ffffff !important",
              fontWeight: "bold"
            }}
          >
            {duplicating ? <CircularProgress size={20} color="inherit" /> : "Duplicar"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {openModal && (
        <CampanhaCreateEdit
          open={openModal}
          onClose={handleClose}
          id={selectedId}
        />
      )}
    </MDBox>
  );
}
